import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { createQuickOptions, getRandomQuickOptions } from '../data/optionsData';
import { fetchBusinessHelpTopics } from '../services/contentService';

const normalizeKey = (value = '') =>
  value
    .toString()
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const QuickOptions = ({ 
  changingOptions, 
  onQuickOption, 
  onViewAll,
  onChangeBusiness,
  isConversationStarted = false 
}) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [topicPhotoMap, setTopicPhotoMap] = useState(new Map());

  // Create quick options with translations and limit to 4
  const allQuickOptions = createQuickOptions(t) || [];
  const quickOptions = getRandomQuickOptions(allQuickOptions, isConversationStarted, true);

  useEffect(() => {
    let isMounted = true;

    const loadTopics = async () => {
      try {
        const data = await fetchBusinessHelpTopics();
        if (!isMounted) return;
        const map = new Map();
        (Array.isArray(data) ? data : []).forEach((topic) => {
          if (topic?.name && topic?.photo?.url) {
            map.set(normalizeKey(topic.name), topic.photo.url);
          }
        });
        setTopicPhotoMap(map);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load Business Help Topics for quick options:', error);
          setTopicPhotoMap(new Map());
        }
      }
    };

    loadTopics();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      maxWidth: '100%'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
      }}>
      <div className="quick-options" style={{ 
        display: 'flex', 
        gap: isConversationStarted ? '12px' : '16px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        overflow: 'hidden'
      }}>
        {quickOptions.map((option, idx) => {
          const lookupKey = normalizeKey(option.key || option.label);
          const photoUrl = topicPhotoMap.get(lookupKey);
          const hasPhoto = Boolean(photoUrl);
          
          return (
            <button
              key={`${option.label}-${idx}-${changingOptions.has(idx) ? 'changing' : 'stable'}`}
              className="option-pill"
              onClick={() => onQuickOption(option.value)}
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                minWidth: isConversationStarted ? '140px' : '180px', 
                minHeight: isConversationStarted ? '120px' : '150px',
                padding: isConversationStarted ? '14px 10px' : '18px 14px', 
                borderRadius: isConversationStarted ? '20px' : '24px', 
                background: changingOptions.has(idx) 
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,0.05) 100%)' 
                  : 'transparent', 
                border: changingOptions.has(idx) 
                  ? '2px solid #22c55e' 
                  : 'none', 
                color: changingOptions.has(idx) ? '#22c55e' : '#374151', 
                fontWeight: 600, 
                fontSize: isConversationStarted ? '11px' : '13px',
                boxShadow: changingOptions.has(idx) 
                  ? '0 4px 12px rgba(34,197,94,0.2)' 
                  : hasPhoto
                    ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                    : '0 2px 6px rgba(0,0,0,0.08)', 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                transform: 'translateY(0)',
                backdropFilter: hasPhoto ? 'blur(10px)' : 'none'
              }}
              onMouseEnter={e => {
                if (!changingOptions.has(idx)) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'; // Simple shadow, no circle effect
                }
              }}
              onMouseLeave={e => {
                if (!changingOptions.has(idx)) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {changingOptions.has(idx) && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(45deg, transparent 30%, rgba(34,197,94,0.08) 50%, transparent 70%)`,
                  animation: 'matrix-sweep 1s linear infinite',
                  zIndex: 1
                }} />
              )}
              
              {/* Mini Popup Card - compact on mobile via .quick-option-card-inner */}
              <div
                className="quick-option-card-inner"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  padding: 0,
                  background: theme.cardBg,
                  border: `1px solid ${theme.primaryBorder}`,
                  borderRadius: '20px',
                  color: theme.primaryText,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  overflow: 'hidden',
                  textAlign: 'left',
                  minHeight: isConversationStarted ? '130px' : '160px',
                  width: isConversationStarted ? '130px' : '160px',
                  marginBottom: isConversationStarted ? '8px' : '12px'
                }}
              >
                {/* Photo Section - Same as popup */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: isConversationStarted ? '80px' : '100px',
                  background: hasPhoto 
                    ? 'transparent'
                    : `linear-gradient(135deg, ${option.iconProps?.color || '#6366f1'}40, ${option.iconProps?.color || '#6366f1'}80)`
                }}>
                  {hasPhoto && (
                    <img
                      src={photoUrl}
                      alt={option.label}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  )}
                  
                  {/* Fallback for no photo — icon only for cleaner look */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: hasPhoto ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 4,
                    color: '#fff'
                  }}>
                    {option.icon && <option.icon size={isConversationStarted ? 16 : 20} color="#fff" />}
                  </div>
                </div>

                {/* Label Section - Only title, no description; spacing for readability */}
                <div style={{ 
                  padding: isConversationStarted ? '12px 10px' : '15px 12px', 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minHeight: 44
                }}>
                  <h3 style={{
                    fontSize: isConversationStarted ? '12px' : '14px',
                    fontWeight: 700,
                    color: theme.primaryText,
                    margin: 0,
                    lineHeight: 1.2,
                    textAlign: 'center'
                  }}>
                    {option.label}
                  </h3>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      
      {/* View All Quick Options Button - Always positioned after quick options */}
      <button
        onClick={onViewAll}
        style={{
          background: isConversationStarted 
            ? theme.accentButton 
            : 'linear-gradient(90deg, #a78bfa 0%, #6366f1 100%)',
          color: '#fff',
          border: 'none',
          borderRadius: isConversationStarted ? '16px' : '12px',
          padding: isConversationStarted ? '8px 16px' : '7px 14px',
          fontSize: isConversationStarted ? '12px' : '13px',
          fontWeight: '600',
          cursor: 'pointer',
          boxShadow: isConversationStarted 
            ? `0 2px 8px ${theme.accentShadow}`
            : '0 2px 8px 0 rgba(99,102,241,0.18)',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          whiteSpace: 'nowrap',
          height: isConversationStarted ? 'auto' : '34px',
          outline: 'none',
          borderBottom: isConversationStarted ? 'none' : '2px solid #7c3aed',
          flexShrink: 0
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = isConversationStarted 
            ? 'translateY(-1px)' 
            : 'translateY(-1px) scale(1.03)';
          e.currentTarget.style.boxShadow = isConversationStarted 
            ? `0 4px 16px ${theme.accentShadow}`
            : '0 4px 12px 0 rgba(99,102,241,0.28)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = isConversationStarted 
            ? `0 2px 8px ${theme.accentShadow}`
            : '0 2px 8px 0 rgba(99,102,241,0.18)';
        }}
      >
        <Plus size={14} />
{t('common.viewAll')}
      </button>

      {/* Change Business Button - Positioned under View More button */}
      {onChangeBusiness && (
        <button
          onClick={onChangeBusiness}
          style={{
            background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            padding: '7px 14px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px 0 rgba(245, 158, 11, 0.18)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            height: '34px',
            outline: 'none',
            borderBottom: '2px solid #d97706',
            flexShrink: 0,
            marginTop: '8px'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-1px) scale(1.03)';
            e.currentTarget.style.boxShadow = '0 4px 12px 0 rgba(245, 158, 11, 0.28)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 2px 8px 0 rgba(245, 158, 11, 0.18)';
          }}
        >
          <span style={{ fontSize: '14px' }}>🏢</span>
          Change Business
        </button>
      )}
      </div>
    </div>
  );
};

export default QuickOptions;